import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpApi } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';
import { useLocationStore } from '@/stores/location-store';
import type { RPListSummary } from './use-receive-payments';

// ── Types ──

export interface SaleTransaction {
  id: string;
  tenantId: string;
  referenceNumber: string;
  customerId: string | null;
  transactionType: string;
  amount: string;
  currency: string;
  memo: string | null;
  paymentMethod: string;
  // ACH fields
  accountType: string | null;
  accountCategory: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
  authorizationType: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  idType: string | null;
  dlState: string | null;
  dlNumber: string | null;
  // Card fields
  cardLastFour: string | null;
  cardHolderName: string | null;
  cardExpirationDate: string | null;
  cardZipCode: string | null;
  cardStreetAddress: string | null;
  // Statement Descriptor
  statementDescriptor: string | null;
  // iStream ACH response
  batchId?: string | null;
  postDate: string | null;
  // Refund / cancellation tracking
  processingAt?: string | null;
  cancelledAt?: string | null;
  refundedAt?: string | null;
  /** ACH refunds that have settled (from GET /sales). */
  refundedAmount?: string | null;
  /** ACH refunds submitted but not settled yet. */
  refundPendingAmount?: string | null;
  // Other
  receiptDelivery: string | null;
  sourceType: string | null;
  status: string;
  statusMessage: string | null;
  createdAt: string;
  updatedAt: string;
  hasAuthDoc: boolean;
}

// ── Queries ──

export function useSaleTransactions(params?: {
  status?: string;
  /** ISO-8601 instants, inclusive, matched against createdAt. */
  startDate?: string;
  endDate?: string;
  postDate?: string;
  paymentMethod?: string;
  perPage?: number;
}) {
  // Scope the transaction list to the currently-selected location so switching
  // location in the header filters the list (legacy location-less sales still show).
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const query = { ...params, ...(selectedLocationId ? { location_id: selectedLocationId } : {}) };
  return useQuery({
    queryKey: ['sales', query],
    queryFn: () =>
      rpApi.get<{
        data: SaleTransaction[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
        summary?: RPListSummary;
      }>('/sales', query as Record<string, string>),
    // ACH approvals happen on a separate customer-facing page, so the merchant's
    // dashboard won't know about them via mutations. Polling every 15 s keeps the
    // Auth Doc column and status badges fresh without requiring a manual refresh.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
}

// ── Mutations ──

export function useCreateSaleTransaction() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      rpApi.post<{ data: SaleTransaction }>('/sales', { ...data, initiatedByEmail: user?.email ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}

export function useResubmitSaleTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpApi.put<{ data: SaleTransaction }>(`/sales/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}

/** iStream ACH: cancel a submitted-but-unsettled sale (backend enforces the 8-hour window). */
export function useCancelSaleTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rpApi.post<{ data: SaleTransaction }>(`/sales/${id}/cancel`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}

/** One refund of an ACH sale (GET /sales/:id/refunds). */
export interface SaleRefund {
  id: string;
  saleId: string;
  amount: string;
  /** Processing → Completed (settled) | Returned | failed */
  status: string;
  statusMessage: string | null;
  postDate: string | null;
  initiatedByEmail: string | null;
  createdAt: string;
}

export interface RefundSaleResult {
  sale: SaleTransaction;
  refund: SaleRefund;
  remainingBalance: string;
}

/**
 * iStream ACH: refund `amount` (dollars, e.g. "12.50") of a settled sale. A sale can be
 * refunded repeatedly until nothing is left. Always an explicit amount, sent to /refunds:
 * a backend that predates partial refunds has no such route and answers 404, rather than
 * ignoring the amount and refunding the whole sale.
 */
export function useRefundSaleTransaction() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: string }) =>
      rpApi.post<{ data: RefundSaleResult }>(`/sales/${id}/refunds`, {
        amount,
        initiatedByEmail: user?.email ?? undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales'] });
      qc.invalidateQueries({ queryKey: ['sale-refunds'] });
    },
  });
}

/** The refunds of a sale, oldest first. Only fetches when `enabled` (i.e. the sale has refund activity). */
export function useSaleRefunds(saleId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['sale-refunds', saleId],
    queryFn: () => rpApi.get<{ data: SaleRefund[] }>(`/sales/${saleId}/refunds`),
    enabled,
    // A refund settles days later via a bank file; keep an open detail view fresh.
    refetchInterval: 15_000,
  });
}

/** A settled refund, as listed under the funding date it posted on (GET /refunds). */
export interface FundingRefund {
  id: string;
  saleId: string;
  /** Reference number of the sale that was refunded. */
  referenceNumber: string;
  firstName: string | null;
  lastName: string | null;
  paymentMethod: string;
  currency: string;
  amount: string;
  status: string;
  postDate: string | null;
  createdAt: string;
}

/**
 * Statuses a sale can have after it was funded. They all count in the funding summary at full
 * amount on the funding day; the refunds themselves are negative lines on the day they posted.
 */
export const FUNDED_SALE_STATUSES = [
  'completed',
  'partially refunded',
  'refunded',
  'refund processing',
  'partial refund processing',
];

/** Refunds that settled on a funding date (the negative lines of that day). */
export function useFundingRefunds(postDate: string, paymentMethod: string) {
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  return useQuery({
    queryKey: ['funding-refunds', postDate, paymentMethod, selectedLocationId],
    queryFn: () =>
      rpApi.get<{ data: FundingRefund[] }>('/refunds', {
        postDate,
        paymentMethod,
        ...(selectedLocationId ? { location_id: selectedLocationId } : {}),
      }),
  });
}

const toCents = (value: string | number | null | undefined) => Math.round(Number(value ?? 0) * 100);
const dollars = (cents: number) => (cents / 100).toFixed(2);

const REFUND_STATUSES = ['partially refunded', 'refunded', 'refund processing', 'partial refund processing'];

/** Whether a sale has any refund to show: totals above zero, or a refund-related status. */
export function hasRefundActivity(sale: Pick<SaleTransaction, 'status' | 'refundedAmount' | 'refundPendingAmount'>) {
  return (
    toCents(sale.refundedAmount) > 0 ||
    toCents(sale.refundPendingAmount) > 0 ||
    REFUND_STATUSES.includes(sale.status?.toLowerCase())
  );
}

/**
 * Where a sale's money stands: refunded (settled), pending (submitted, not settled), and what
 * is still available to refund. Pending refunds are held against the available balance.
 */
export function getRefundableBalance(
  sale: Pick<SaleTransaction, 'amount' | 'refundedAmount' | 'refundPendingAmount'>,
): {
  refunded: string;
  pending: string;
  remaining: string;
  remainingCents: number;
} {
  const refundedCents = toCents(sale.refundedAmount);
  const pendingCents = toCents(sale.refundPendingAmount);
  const remainingCents = Math.max(0, toCents(sale.amount) - refundedCents - pendingCents);
  return {
    refunded: dollars(refundedCents),
    pending: dollars(pendingCents),
    remaining: dollars(remainingCents),
    remainingCents,
  };
}

/**
 * Checks a refund amount typed by the user against the balance. Returns an error message,
 * or null when the amount is valid. The backend re-checks this.
 */
export function validateRefundAmount(input: string, remainingCents: number): string | null {
  const value = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(value) || toCents(value) <= 0) {
    return 'Enter an amount greater than $0 with at most 2 decimal places.';
  }
  if (toCents(value) > remainingCents) {
    return `Amount can't exceed the remaining balance of $${(remainingCents / 100).toFixed(2)}.`;
  }
  return null;
}

/** Mirrors the backend's cancellation window (cancelSaleACHService). */
export const ACH_CANCEL_WINDOW_MS = 8 * 60 * 60 * 1000;

/**
 * Whether the Cancel / Refund actions apply to a sale row. The backend remains the
 * authority (it also enforces provider = iStream and the window); this only decides
 * which buttons to show.
 */
export function getSaleReversalActions(sale: SaleTransaction, now = Date.now()) {
  const isAch = sale.paymentMethod?.toLowerCase() === 'ach';
  const status = sale.status?.toLowerCase();
  const processingAtMs = sale.processingAt ? new Date(sale.processingAt).getTime() : NaN;
  return {
    canCancel:
      isAch &&
      status === 'processing' &&
      Number.isFinite(processingAtMs) &&
      now - processingAtMs <= ACH_CANCEL_WINDOW_MS,
    // 'Refund Processing' means everything left is already being refunded
    canRefund:
      isAch &&
      (status === 'completed' || status === 'partially refunded' || status === 'partial refund processing') &&
      getRefundableBalance(sale).remainingCents > 0,
  };
}
